import React from 'react'
import { User, Users, Calendar, Hash } from 'lucide-react'
import { GameAnalysisFilters } from '@/redux/slices/gameAnalysisSlice'
import { getPlayerName } from '@/utils/playerUtils'
import { GameSummaryMetadata } from '@/redux/types/room'

export enum FilterType {
  PLAYER = 'player',
  TEAM = 'team',
  PHASE = 'phase',
  ROUND = 'round',
}

export interface FilterDisplayItem {
  type: FilterType
  label: string
  icon: React.ReactNode
  colorClass: string
  onRemove?: () => void
}

export interface FilterDisplayItemWithRemove extends FilterDisplayItem {
  onRemove: () => void
}

export const convertFiltersToDisplayItems = <T extends boolean = false>(
  filters: GameAnalysisFilters | undefined,
  metadata: GameSummaryMetadata | null,
  includeRemove: T = false as T,
  onRemoveCallback?: (type: FilterType, id: number) => () => void
): T extends true ? FilterDisplayItemWithRemove[] : FilterDisplayItem[] => {
  if (!filters || !metadata) return []

  const items: FilterDisplayItem[] = []

  // Players
  filters.player_ids?.forEach((playerId) => {
    const player = metadata.players.find((p) => p.member__id === playerId)
    if (player) {
      const item: FilterDisplayItem = {
        type: FilterType.PLAYER,
        label: getPlayerName(player),
        icon: <User className='h-3 w-3' />,
        colorClass: includeRemove
          ? 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100'
          : 'bg-sky-50 text-sky-700 border-sky-200',
      }
      if (includeRemove && onRemoveCallback) {
        item.onRemove = onRemoveCallback(FilterType.PLAYER, playerId)
      }
      items.push(item)
    }
  })

  // Teams
  filters.team_ids?.forEach((teamId) => {
    const team = metadata.teams.find((t) => t.id === teamId)
    if (team) {
      const item: FilterDisplayItem = {
        type: FilterType.TEAM,
        label: team.name,
        icon: <Users className='h-3 w-3' />,
        colorClass: includeRemove
          ? 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
          : 'bg-purple-50 text-purple-700 border-purple-200',
      }
      if (includeRemove && onRemoveCallback) {
        item.onRemove = onRemoveCallback(FilterType.TEAM, teamId)
      }
      items.push(item)
    }
  })

  // Phases
  filters.phase_ids?.forEach((phaseId) => {
    const phase = metadata.phases.find((p) => p.id === phaseId)
    if (phase) {
      const item: FilterDisplayItem = {
        type: FilterType.PHASE,
        label: `Round ${phase.game_round__round_number} - ${phase.phase_type}`,
        icon: <Calendar className='h-3 w-3' />,
        colorClass: includeRemove
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
          : 'bg-emerald-50 text-emerald-700 border-emerald-200',
      }
      if (includeRemove && onRemoveCallback) {
        item.onRemove = onRemoveCallback(FilterType.PHASE, phaseId)
      }
      items.push(item)
    }
  })

  // Rounds
  filters.round_numbers?.forEach((roundNumber) => {
    const round = metadata.rounds.find((r) => r.round_number === roundNumber)
    if (round) {
      const item: FilterDisplayItem = {
        type: FilterType.ROUND,
        label: `Round ${round.round_number}`,
        icon: <Hash className='h-3 w-3' />,
        colorClass: includeRemove
          ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
          : 'bg-amber-50 text-amber-700 border-amber-200',
      }
      if (includeRemove && onRemoveCallback) {
        item.onRemove = onRemoveCallback(FilterType.ROUND, roundNumber)
      }
      items.push(item)
    }
  })

  return items as T extends true
    ? FilterDisplayItemWithRemove[]
    : FilterDisplayItem[]
}
